import { html, render } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { customElement, property, queryAll, query, state } from "lit/decorators.js";
import { Button, ColorTypes, OmegaComponent, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { markdownToHtml } from "@omegagrid/markdown";
import rp, { AppComponent } from "@reporting/rp-app-host";
import { ChatMessage } from "@reporting/rp-types/ai";
import { ContextData } from "@reporting/rp-types/analytics";
import { attachmentIcon, isImageAttachment } from "../attachments";
import { extractContextDataFromChatMessage } from "../helpers";
import { ChatAttachmentRef } from "../types";
import { style } from "./chatMessage.style";
import { AnalyticsApplication } from "@reporting/rp-mf-analytics";

/**
 * One entry of the worker's run trace (APIService::executeChatCompletion): a completed model
 * round-trip, or — while the answer is being generated — the step in flight, flagged `running`
 * with its `phase` ('model' = waiting for the provider, 'tool' = executing a tool call).
 */
interface TraceStep {
	iteration?: number;
	tokens?: { input?: number; output?: number; cache_write?: number; cache_read?: number; total?: number };
	thinking?: string | null;
	tools?: { name: string; arguments?: string; result_size?: number; duplicate?: boolean }[];
	duration_ms?: number;
	forced_final?: boolean;
	running?: boolean;
	phase?: 'model' | 'tool';
	tool?: string;
	tool_index?: number;
	tool_count?: number;
	attempt?: number;
	attempts?: number;
}

@customElement('rp-mf-ai-chatmessage')
export class ChatMessageComponent extends OmegaComponent {

	static styles = [OmegaComponent.styles, style];

	/** after this many seconds without a worker heartbeat the progress shows a "maybe stuck" warning */
	private static readonly STALE_SECONDS = 60;

	@property({type: Object})
	message: ChatMessage;

	@property({type: Boolean})
	autoLoadView = false;

	@queryAll('.view-placeholder')
	viewPlaceholders: NodeListOf<HTMLDivElement>;

	@query('.message-content')
	messageContent: HTMLDivElement;

	_contextData: ContextData[] = [];

	@state()
	private _renderedHtml: unknown = '';

	private _viewObserver?: ResizeObserver;
	private _viewVisible = new WeakMap<Element, boolean>();
	private _observedViews = new Set<AppComponent>();

	get isLoading() {
		return Boolean((this.message as any)?.loading)
			|| this.message?.status === 'pending'
			|| this.message?.status === 'processing';
	}

	willUpdate(props: Map<string, any>) {
		if (props.has('message')) {
			this._contextData = [];
			this._resetViewObserver(); // drop stale views before the placeholders are re-rendered
			if (!this.isLoading) this._renderMessage();
			else this._renderedHtml = '';
		}
	}

	connectedCallback() {
		super.connectedCallback();
		// Switching apps in the left sidebar removes this component from the DOM
		// so we need to re-observe any embedded views when it comes back.
		this._viewVisible = new WeakMap();
		this._observedViews.forEach(view => this._observeView(view));
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this._viewObserver?.disconnect();
	}

	// ---- live progress of an answer being generated (worker trace + heartbeat, see ChatService::findMessages) ----

	private _traceCache?: { raw: string; steps: TraceStep[] };

	private get _trace(): TraceStep[] {
		const raw = this.message?.trace ?? '';
		if (this._traceCache?.raw !== raw) {
			let steps: TraceStep[] = [];
			try {
				const parsed = raw ? JSON.parse(raw) : [];
				if (Array.isArray(parsed)) steps = parsed;
			} catch {}
			this._traceCache = { raw, steps };
		}
		return this._traceCache.steps;
	}

	/** seconds since the worker last reported (server clock); the message age while it is still unclaimed */
	private get _staleFor(): number | undefined {
		return this.message?.heartbeat_age ?? this.message?.elapsed ?? undefined;
	}

	private _renderProgress() {
		const steps = this._trace;
		const running = steps.find(s => s.running);
		const done = steps.filter(s => !s.running);
		const status = this.message?.status;
		const stale = this._staleFor;

		let label: string;
		if (!status || status === 'pending' || !running) label = msg('Starting') + '…';
		else if (running.phase === 'tool') label = msg('ai.progress.tool', { data: { tool: running.tool ?? '?', index: running.tool_index ?? 1, count: running.tool_count ?? 1 } });
		else if (running.forced_final) label = msg('ai.progress.final');
		else label = msg('ai.progress.model', { data: { step: (running.iteration ?? done.length) + 1 } });
		if (running?.attempt) label += ` · ${msg('ai.progress.attempt', { data: { attempt: running.attempt, attempts: running.attempts ?? '?' } })}`;

		return html`
			<div class="progress">
				${done.map(step => this._renderStep(step))}
				${running?.phase === 'tool' ? this._renderStep(running) : ''}
				<div class="progress-current">
					<img class="progress-loader" src="${rp.options.staticUrl}/assets/loader.webp" />
					<span>${label}</span>
				</div>
				${stale != null && stale >= ChatMessageComponent.STALE_SECONDS ? html`
					<div class="progress-stale">⚠️ ${msg('ai.progress.stale', { data: { seconds: stale } })}</div>
				` : ''}
			</div>
		`;
	}

	// A model round-trip: duration, output tokens, the tools it called, its thinking (collapsed).
	// Also used for the step in flight while its tool calls run (tools executed so far).
	private _renderStep(step: TraceStep) {
		const tools = step.tools ?? [];
		return html`
			<div class="progress-step">
				<div class="progress-step-head">
					<span>${msg('ai.progress.step', { data: { step: (step.iteration ?? 0) + 1 } })}</span>
					${step.duration_ms != null ? html`<span class="progress-muted">${(step.duration_ms / 1000).toFixed(1)} s</span>` : ''}
					${step.tokens?.output ? html`<span class="progress-muted">${step.tokens.output.toLocaleString()} out</span>` : ''}
					${tools.length ? html`<span>🔧 ${tools.map((t, j) => html`${j ? ', ' : ''}<span title=${t.arguments ?? ''}>${t.name}${t.duplicate ? ' (dup)' : ''}</span>`)}</span>` : ''}
				</div>
				${step.thinking ? html`
					<details class="progress-thinking">
						<summary>${msg('ai.progress.thinking')}</summary>
						<div>${step.thinking}</div>
					</details>
				` : ''}
			</div>
		`;
	}

	// Re-layout the embedded view whenever it transitions from hidden (0×0) to visible
	private _observeView(view: AppComponent) {
		this._observedViews.add(view);
		this._viewObserver ??= new ResizeObserver(entries => {
			for (const entry of entries) {
				const { width, height } = entry.contentRect;
				const visible = width > 0 && height > 0;
				const wasVisible = this._viewVisible.get(entry.target) ?? false;
				this._viewVisible.set(entry.target, visible);
				if (visible && !wasVisible) (entry.target as AppComponent).layout?.();
			}
		});
		this._viewObserver.observe(view);
	}

	private _resetViewObserver() {
		this._viewObserver?.disconnect();
		this._observedViews.clear();
		this._viewVisible = new WeakMap();
	}

	updated(props: Map<string, any>) {
		this.viewPlaceholders.forEach(placeholder => {
			if (placeholder.dataset.initialized) return;
			placeholder.dataset.initialized = '1';
			const index = Number(placeholder.dataset.index);
			if (rp.host.compact) this._renderOpenInTabButton(placeholder, index);
			else if (this.autoLoadView) this._loadView(placeholder, index);
			else this._renderLoadButton(placeholder, index);
		});
	}

	// Compact (mobile) hosts get no inline view: a 300 px report in a narrow chat is unusable, it opens in its own tab.
	private _renderOpenInTabButton(placeholder: HTMLDivElement, index: number) {
		dom.empty(placeholder);
		placeholder.appendChild(dom.createElement<Button>('og-button', {
			icon: 'window',
			text: msg('Open in new tab'),
			color: ColorTypes.Transparent,
			listeners: [['click', async () => {
				const app = await rp.app('analytics') as AnalyticsApplication;
				const ctx = await app.contextBroker.getContext({ context: this._contextData[index] });
				rp.host.open(`analytics-reportContainer-${ctx.id_context}`, {
					title: ctx.name || `#${ctx.id_context}`
				});
			}]]
		}));
	}

	// Renders a button that loads the analytics view on demand (older messages).
	private _renderLoadButton(placeholder: HTMLDivElement, index: number) {
		dom.empty(placeholder);
		placeholder.appendChild(dom.createElement<Button>('og-button', {
			icon: 'eye',
			text: msg('Show view'),
			color: ColorTypes.Transparent,
			listeners: [['click', () => this._loadView(placeholder, index)]]
		}));
	}

	// Spins up the analytics report component for the given context and fills the placeholder.
	private async _loadView(placeholder: HTMLDivElement, index: number) {
		const app = await rp.app('analytics') as AnalyticsApplication;
		const ctx = await app.contextBroker.getContext({ context: this._contextData[index] });
		const componentId = `analytics-reportContainer-${ctx.id_context}`;

		dom.empty(placeholder);
		const reportWrapper = dom.createElement('div');
		dom.setSize(reportWrapper, { h: 300 });
		const view = await app.createComponent(componentId);
		reportWrapper.appendChild(view);
		placeholder.appendChild(reportWrapper);
		this._observeView(view);

		placeholder.appendChild(dom.createElement<Button>('og-button', {
			icon: 'window-restore',
			text: msg('Open in dialog'),
			color: ColorTypes.Transparent,
			listeners: [['click', async () => {
				rp.host.openDialog({
					id: componentId,
					caption: ctx.name || `#${ctx.id_context}`,
					closable: true,
					resizable: true,
					location: 'center',
					width: Math.min(800, rp.host.offsetWidth * 0.8),
					height: Math.min(600, rp.host.offsetHeight * 0.8),
				});
			}]]
		}));

		placeholder.appendChild(dom.createElement<Button>('og-button', {
			icon: 'window',
			text: msg('Open in new tab'),
			color: ColorTypes.Transparent,
			listeners: [['click', async () => {
				rp.host.open(componentId, {
					title: ctx.name || `#${ctx.id_context}`
				});
			}]]
		}));
	}

	private async _renderMessage() {
		if (!this.message) { this._renderedHtml = ''; return; }
		if (this.message.status === 'cancelled') {
			this._renderedHtml = msg('ai.progress.cancelled');
			return;
		}
		if (!this.message.content) {
			this._renderedHtml = this.message.role === 'assistant'
				? '⚠️ ' + msg('ai.nocontent')
				: '';
			return;
		}

		const contexts = extractContextDataFromChatMessage(this.message);
		const chunks: string[] = [];
		let index = 0;

		for (let i = 0; i < contexts.length; i++) {
			const context = contexts[i];
			if (context.from > index) {
				const content = this.message.content.substring(index, context.from);
				chunks.push(this.message.role == 'assistant' ? await markdownToHtml(content) : content);
			}
			this._contextData.push(context.data);
			chunks.push(`<div class="view-placeholder" data-index="${i}"><p style="text-align: center">${msg('Loading')}...</p></div>`);
			index = context.to;
		}

		if (index < this.message.content.length) {
			const content = this.message.content.substring(index);
			chunks.push(this.message.role == 'assistant' ? await markdownToHtml(content) : content);
		}

		this._renderedHtml = unsafeHTML(chunks.join(''));
	}

	get attachments(): ChatAttachmentRef[] {
		return ((this.message as any)?.attachments as ChatAttachmentRef[]) ?? [];
	}

	// Persisted attachments are fetched by id (cookie-authenticated <img src>); optimistic
	// ones (just sent, not yet saved) only have the inline base64 payload.
	private _attachmentSrc(att: ChatAttachmentRef): string {
		if (att.idz) return rp.ms('ai').createUrl(`attachments/${att.idz}`);
		if (att.data) return `data:${att.media_type};base64,${att.data}`;
		return '';
	}

	private _openImage(src: string, name?: string) {
		const component = dom.createElement('div');
		component.style.cssText = 'display:flex;align-items:center;justify-content:center;height:100%;padding:8px;overflow:auto;';
		const img = dom.createElement<HTMLImageElement>('img');
		img.src = src;
		img.style.cssText = 'max-width:100%;max-height:100%;object-fit:contain;';
		component.appendChild(img);
		rp.host.openDialog({
			closable: true,
			resizable: true,
			location: 'center',
			width: Math.min(900, rp.host.offsetWidth * 0.8),
			height: Math.min(700, rp.host.offsetHeight * 0.8),
			caption: name || msg('Detail'),
			component,
		});
	}

	// Attachments close the message: an image as a clickable thumbnail, a text file as a download
	// link (the server sends it with Content-Disposition: attachment).
	private _renderAttachments() {
		const attachments = this.attachments;
		if (!attachments.length) return '';
		return html`
			<div class="message-attachments">
				${attachments.map(att => {
					const src = this._attachmentSrc(att);
					if (!src) return '';
					return isImageAttachment(att) ? html`
						<img
							class="message-attachment"
							src="${src}"
							alt="${att.file_name ?? ''}"
							title="${att.file_name ?? ''}"
							@click="${() => this._openImage(src, att.file_name)}" />
					` : html`
						<a class="message-attachment-file" href="${src}" download="${att.file_name ?? ''}" title="${att.file_name ?? ''}">
							<og-icon icon="${attachmentIcon(att)}"></og-icon>
							<span>${att.file_name ?? ''}</span>
							<og-icon icon="download"></og-icon>
						</a>
					`;
				})}
			</div>
		`;
	}

	// Links inside rendered assistant markdown are intercepted here. A wiki reference is a
	// bare page name as the link target (see WikiTool prompt) — open it in-app without a full
	// reload. Anything with an explicit scheme (http:, mailto: …) opens in a new tab.
	private _onContentClick = async (e: MouseEvent) => {
		const anchor = (e.target as HTMLElement)?.closest?.('a') as HTMLAnchorElement | null;
		if (!anchor || anchor.classList.contains('message-attachment-file')) return; // attachment downloads are plain links
		const href = anchor.getAttribute('href');
		if (!href || href.startsWith('#')) return;
		e.preventDefault();
		if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//')) {
			window.open(href, '_blank');
			return;
		}
		// internal documentation link: the destination is the wiki page name (tolerate a
		// stray host/`wiki/` prefix in case the model still emits the old URL format).
		const name = href.replace(/^\/?(?:[\w.-]+\/)*wiki\//i, '');
		const wiki = await rp.app('wiki');
		await wiki?.openPath?.(name);
	}

	get modelName() {
		if (!this.message?.model) return '';
		const parts = this.message.model.split('/');
		let name = parts[parts.length - 1];
		return name.replace(/\d\d\d\d\-\d\d\-\d\d$/, '').replace(/\-$/, '');
	}

	_openMessageDetail = () => {
		let response: any;
		try {
			response = JSON.parse(this.message.response.replace(/\n/g, '').trim());
		} catch {}

		// Per-iteration trace of the whole run (tokens, tools, thinking) — see APIService.
		let trace: any[] = [];
		try {
			const raw = this.message.trace;
			if (raw) trace = JSON.parse(raw);
		} catch {}
		if (!Array.isArray(trace)) trace = [];

		// Nothing structured to show.
		if ((!response || typeof response !== 'object') && !trace.length) return;

		const model = response?.model || this.message.model;
		const fmt = (n: any) => (n === undefined || n === null || n === '') ? '-' : Number(n).toLocaleString();

		// Token totals summed across every tool-call iteration — the raw `response`
		// only carries the final round. Older messages without a trace fall back to it.
		const sum = (k: string) => trace.reduce((a, t) => a + (Number(t?.tokens?.[k]) || 0), 0);
		// Full prompt the model read = fresh input + cached reads + cache writes. Anthropic's
		// input_tokens excludes cached tokens, so fresh-only looks deceptively small on a cache
		// hit; the Cache row below breaks down how much of this was served from / written to cache.
		const promptTokens = trace.length ? sum('input') + sum('cache_read') + sum('cache_write') : response?.usage?.prompt_tokens;
		const completionTokens = trace.length ? sum('output') : response?.usage?.completion_tokens;
		const toolCount = trace.reduce((a, t) => a + (Array.isArray(t?.tools) ? t.tools.length : 0), 0);

		// Final-message reasoning for OpenAI-shape providers (Anthropic thinking comes from the trace).
		const choice = response?.choices?.[0]?.message;
		const fallbackReasoning = choice?.reasoning || choice?.reasoning_content;

		const component = dom.createElement('div');
		component.style.padding = '16px';
		component.style.userSelect = 'text';
		render(html`
			<table>
				<tr><td><strong>${msg('Model')}</strong></td><td>${model || '?'}</td></tr>
				<tr><td><strong>${msg('Tokens')}</strong></td><td>${fmt(this.message.total_tokens)}</td></tr>
				<tr><td><strong>${msg('Prompt')}</strong></td><td>${fmt(promptTokens)}</td></tr>
				<tr><td><strong>${msg('Completion')}</strong></td><td>${fmt(completionTokens)}</td></tr>
				${trace.length ? html`
					<tr><td><strong>${msg('Cache')}</strong></td><td>${fmt(sum('cache_read'))} / ${fmt(sum('cache_write'))}</td></tr>
					<tr><td><strong>${msg('Steps')}</strong></td><td>${trace.length}</td></tr>
					<tr><td><strong>${msg('Tool calls')}</strong></td><td>${toolCount}</td></tr>
				` : html`
					<tr><td><strong>${msg('Thinking')}</strong></td><td>${fmt(response?.usage?.completion_tokens_details?.reasoning_tokens)}</td></tr>
				`}
			</table>
			${trace.length ? html`
				<div style="margin-top:16px;display:flex;flex-direction:column;gap:12px;">
					${trace.map((step: any, i: number) => {
						const tools = Array.isArray(step?.tools) ? step.tools : [];
						if (!step?.thinking && !tools.length) return '';
						return html`
							<div>
								<div style="opacity:.6;font-size:.8em;">
									${msg('Step')} ${i + 1}${step?.duration_ms != null ? ` · ${(step.duration_ms / 1000).toFixed(1)}s` : ''}${step?.tokens?.output ? ` · ${fmt(step.tokens.output)} out` : ''}${step?.running ? ` · ${msg('ai.progress.interrupted')}` : ''}
								</div>
								${tools.length ? html`
									<div style="font-size:.85em;margin:2px 0;">
										🔧 ${tools.map((t: any, j: number) => html`${j ? ', ' : ''}<span title=${t.arguments ?? ''}>${t.name}${t.duplicate ? ' (dup)' : ''}</span>`)}
									</div>
								` : ''}
								${step?.thinking ? html`
									<div style="white-space:pre-wrap;padding:8px;border-radius:4px;background:rgba(127,127,127,.1);font-size:.9em;">${step.thinking}</div>
								` : ''}
							</div>
						`;
					})}
				</div>
			` : (fallbackReasoning ? html`
				<div style="margin-top:16px;">
					<strong>${msg('Thinking')}</strong>
					<div style="white-space:pre-wrap;">${fallbackReasoning}</div>
				</div>
			` : '')}
		`, component);

		rp.host.openDialog({
			closable: true,
			resizable: true,
			scrollable: true,
			location: 'center',
			width: 700,
			height: 500,
			caption: msg('Detail'),
			component
		});
	}

	render = () => this.message ? html`
		<div class="message">
			<div class="message-left">
				<div class="message-avatar">
					${this.message.role == 'assistant' ? html`
						<og-button icon="robot" @click=${this._openMessageDetail} title=${msg('Detail')} color="transparent"></og-button>
					` : html`
						<og-icon icon="user"></og-icon>
					`}
				</div>
				<div class="message-role-name">${this.message.role == 'user'
					? (this.message.login_name ?? this.message.user_name)
					: this.modelName}</div>
			</div>

			<div class="message-content" @click=${this._onContentClick}>
				${this.isLoading ? this._renderProgress() : this._renderedHtml}
				${this._renderAttachments()}
			</div>
		</div>
	` : '';

}
